type EntityBucketKey = 'groupId' | 'deviceId' | 'gatewayId';

type SelectedWidgetEntity = {
    deviceId: string;
    entityIds: string[];
};

type CustomEntity = {
    widgetType: string;
    dependentEntities: string[];
};

type SelectedEntitiesPayload = {
    groupId: string[];
    deviceId: string[];
    gatewayId: string[];
    customEntities: CustomEntity[];
    selectedWidgetEntities: SelectedWidgetEntity[];
};

type BuildSelectedEntitiesInput = {
    widgetTypeId?: string;
    rawConfig?: unknown;
    cleanedConfig?: unknown;
    sourceId?: string | null;
};

type PayloadNode = {
    id?: string;
    type?: string;
    deviceId?: string;
    groupId?: string;
    gatewayId?: string;
    wrapperDeviceType?: string;
    loadId?: string;
    channelId?: string;
    entityIds?: string[];
};

type DeviceEntityMap = Record<string, Set<string>>;

const toObject = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const toArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const toStringArray = (value: unknown): string[] =>
    toArray(value)
        .map((item) => (typeof item === 'string' ? item : ''))
        .filter(Boolean);

const appendUnique = (target: string[], values: string[]): void => {
    const known = new Set(target);
    values.forEach((value) => {
        if (!known.has(value)) {
            known.add(value);
            target.push(value);
        }
    });
};

const addMapValue = (map: DeviceEntityMap, deviceId: string, entityId: string): void => {
    if (!deviceId || !entityId) return;
    if (!map[deviceId]) {
        map[deviceId] = new Set<string>();
    }
    map[deviceId].add(entityId);
};

const addFilterValues = (
    filters: Record<string, unknown>,
    bucket: SelectedEntitiesPayload,
    key: EntityBucketKey,
    sourceKeys: string[]
): void => {
    sourceKeys.forEach((sourceKey) => {
        const rawValue = filters[sourceKey];
        const values = Array.isArray(rawValue)
            ? toStringArray(rawValue)
            : typeof rawValue === 'string'
            ? [rawValue]
            : [];
        appendUnique(bucket[key], values);
    });
};

const classifyNodeBucket = (node: PayloadNode): EntityBucketKey | null => {
    const nodeType = (node?.type ?? '').toLowerCase();
    const wrapperType = (node?.wrapperDeviceType ?? '').toLowerCase();

    if (nodeType.includes('gateway') || wrapperType === 'gateway') return 'gatewayId';
    if (nodeType.includes('group')) return 'groupId';
    if (nodeType.includes('device')) return 'deviceId';
    return null;
};

const collectDeviceEntityFromChannels = (entry: unknown, map: DeviceEntityMap): void => {
    const configEntry = toObject(entry);
    const deviceId = typeof configEntry.deviceId === 'string' ? configEntry.deviceId : '';
    const channels = toArray(configEntry.channels);

    if (channels.length > 0) {
        // Nested format used by details / properties / settings / command_bar:
        // { deviceId, channels: [{ channelId | loadId }] }
        channels.forEach((channel) => {
            const channelObj = toObject(channel);
            const channelId = typeof channelObj.channelId === 'string' ? channelObj.channelId : '';
            const loadId = typeof channelObj.loadId === 'string' ? channelObj.loadId : '';
            const entityId = channelId || loadId;
            addMapValue(map, deviceId, entityId);
        });
        return;
    }

    // Flat format used by charts / trends:
    // { deviceId, channelId }
    const channelId = typeof configEntry.channelId === 'string' ? configEntry.channelId : '';
    const loadId = typeof configEntry.loadId === 'string' ? configEntry.loadId : '';
    const entityId = channelId || loadId;
    addMapValue(map, deviceId, entityId);
};

export const useWidgetSelectedEntities = (): {
    buildSelectedEntities: (args: BuildSelectedEntitiesInput) => SelectedEntitiesPayload;
} => {
    const buildSelectedEntities = ({
        widgetTypeId,
        rawConfig,
        cleanedConfig,
        sourceId,
    }: BuildSelectedEntitiesInput): SelectedEntitiesPayload => {
        const rawConfigObj = toObject(rawConfig);
        const cleanedConfigObj = toObject(cleanedConfig);
        const filters = toObject(rawConfigObj.filters);
        const selectedNodes = toArray(rawConfigObj.selectedNodes) as PayloadNode[];

        const selectedEntities: SelectedEntitiesPayload = {
            groupId: [],
            deviceId: [],
            gatewayId: [],
            customEntities: [],
            selectedWidgetEntities: [],
        };

        addFilterValues(filters, selectedEntities, 'groupId', ['groupId', 'groupIds', 'groups', 'relativeGroupId']);
        addFilterValues(filters, selectedEntities, 'deviceId', ['deviceId', 'deviceIds']);
        addFilterValues(filters, selectedEntities, 'gatewayId', ['gatewayId', 'gatewayIds', 'parentId']);

        selectedNodes.forEach((node) => {
            const bucket = classifyNodeBucket(node);
            const nodeId = typeof node?.id === 'string' ? node.id : '';

            if (bucket && nodeId) {
                appendUnique(selectedEntities[bucket], [nodeId]);
            }

            if (typeof node?.deviceId === 'string') {
                appendUnique(selectedEntities.deviceId, [node.deviceId]);
            }
            if (typeof node?.groupId === 'string') {
                appendUnique(selectedEntities.groupId, [node.groupId]);
            }
            if (typeof node?.gatewayId === 'string') {
                appendUnique(selectedEntities.gatewayId, [node.gatewayId]);
            }
        });

        if (typeof sourceId === 'string' && sourceId.length && !selectedEntities.deviceId.length) {
            appendUnique(selectedEntities.deviceId, [sourceId]);
        }

        const deviceEntityMap: DeviceEntityMap = {};

        toArray(rawConfigObj.secondary).forEach((entry) => collectDeviceEntityFromChannels(entry, deviceEntityMap));
        toArray(rawConfigObj.primary).forEach((entry) => collectDeviceEntityFromChannels(entry, deviceEntityMap));

        const gaugeData = toObject(rawConfigObj.data);
        if (typeof gaugeData.deviceId === 'string' && typeof gaugeData.channelId === 'string') {
            addMapValue(deviceEntityMap, gaugeData.deviceId, gaugeData.channelId);
        }

        selectedNodes.forEach((node) => {
            const deviceId = typeof node?.deviceId === 'string' ? node.deviceId : '';
            const loadId = typeof node?.loadId === 'string' ? node.loadId : '';
            if (deviceId && loadId) {
                addMapValue(deviceEntityMap, deviceId, loadId);
            }
        });

        toArray(cleanedConfigObj.selectedNodes).forEach((entry) => {
            const entryObj = toObject(entry);
            const deviceId = typeof entryObj.deviceId === 'string' ? entryObj.deviceId : '';
            const loadId = typeof entryObj.loadId === 'string' ? entryObj.loadId : '';
            if (deviceId && loadId) {
                addMapValue(deviceEntityMap, deviceId, loadId);
            }
        });

        selectedEntities.selectedWidgetEntities = Object.entries(deviceEntityMap)
            .filter(([, entitySet]) => entitySet.size > 0)
            .map(([deviceId, entitySet]) => ({
                deviceId,
                entityIds: Array.from(entitySet),
            }));

        if (widgetTypeId === 'load_groups' || widgetTypeId === 'load_group_trends') {
            // Load group IDs are globally unique — do not populate deviceId / groupId / gatewayId.
            selectedEntities.deviceId = [];
            selectedEntities.groupId = [];
            selectedEntities.gatewayId = [];
            selectedEntities.selectedWidgetEntities = [];

            // dependentEntities = the load group IDs themselves (secondary[*].id for non-multiDevice,
            // selectedNodes[*].id for multiDevice)
            const dependentEntities = Array.from(
                new Set([
                    // multiDevice: load group IDs come from selectedNodes[*].id
                    ...selectedNodes.map((node) => (typeof node?.id === 'string' ? node.id : '')).filter(Boolean),
                    // non-multiDevice: selectedNodes is deleted by handleCleanLoadGroupsPayload,
                    // so collect load group IDs from secondary[*].id
                    ...toArray(rawConfigObj.secondary)
                        .map((item) => {
                            const obj = toObject(item);
                            return typeof obj.id === 'string' ? obj.id : '';
                        })
                        .filter(Boolean),
                ])
            );

            if (dependentEntities.length) {
                selectedEntities.customEntities.push({
                    widgetType: 'loadGroup',
                    dependentEntities,
                });
            }
        }

        return selectedEntities;
    };

    return {
        buildSelectedEntities,
    };
};
